import React from 'react';
import { StyleSheet, View } from 'react-native';
import { ButtonComponentAnalytics } from 'shared/analytics/types/componentEvents';
import { ButtonTypes } from 'shared/analytics/types/componentTypes';
import PressableWithDelay from 'shared/components/PressableWithDelay/PressableWithDelay';
import Illustration, {
  IllustrationKeyType,
} from 'shared/components/vxl/Illustration/Illustration';
import ButtonText from 'shared/components/vxl/typography/component/ButtonText';
import BodySmText from 'shared/components/vxl/typography/primary/body/BodySmText';
import TitleSmText from 'shared/components/vxl/typography/primary/title/TitleSmText';
import { useComponentAnalytics } from 'shared/hooks/analytics/useComponentAnalytics';
import { PressableAnalyticsProps } from 'shared/hooks/analytics/usePressableAnalytics';
import theme from 'shared/styles/theme';

export interface ProductOfferCardProps extends PressableAnalyticsProps {
  titleText: string;
  bodyText?: string;
  buttonText: string;
  onPress: (...args: unknown[]) => unknown;
  illustrationKey: IllustrationKeyType;
  illustrationBackgroundColor: string;
  isDisabled?: boolean;
  isLoading?: boolean;
  hasBorder?: boolean;
  accessibilityLabel?: string;
  testID?: string;
  componentAnalytics?: ButtonComponentAnalytics;
}

const ProductOfferCard = ({
  titleText,
  bodyText,
  buttonText,
  onPress,
  illustrationKey,
  illustrationBackgroundColor,
  isDisabled = false,
  isLoading = false,
  hasBorder = false,
  accessibilityLabel,
  testID = '',
  analyticsId = undefined,
  customAttributes = null,
  componentAnalytics,
}: ProductOfferCardProps) => {
  const { logPressableClicked } = useComponentAnalytics();

  const handlePress = () => {
    if (componentAnalytics) {
      logPressableClicked({
        pressable_name: componentAnalytics.pressableName,
        component_type: ButtonTypes.SECONDARY,
        category: componentAnalytics.category,
        sub_category: componentAnalytics.subCategory,
      });
    }

    onPress();
  };

  return (
    <PressableWithDelay
      style={({ pressed, hovered, focused }) => [
        styles.container,
        hasBorder && styles.border,
        hovered && styles.hovered,
        focused && styles.focused,
        pressed && styles.pressed,
      ]}
      onPress={handlePress}
      disabled={isDisabled || isLoading}
      testID={testID}
      analyticsId={analyticsId}
      customAttributes={customAttributes}
      role="button"
      accessible
      accessibilityLabel={accessibilityLabel ? accessibilityLabel : buttonText}
      aria-disabled={isDisabled}
    >
      <View style={styles.textContainer}>
        <TitleSmText style={isDisabled && styles.disabledText}>
          {titleText}
        </TitleSmText>
        {bodyText ? (
          <BodySmText style={isDisabled && styles.disabledText}>
            {bodyText}
          </BodySmText>
        ) : null}
        <View
          style={[
            styles.buttonTextContainer,
            isDisabled && styles.disabledBorder,
          ]}
        >
          <ButtonText style={isDisabled && styles.disabledText}>
            {buttonText}
          </ButtonText>
        </View>
      </View>
      <View style={styles.illustrationContainer}>
        <View
          style={[
            styles.illustrationBackground,
            { backgroundColor: illustrationBackgroundColor },
          ]}
        />
        <View style={styles.illustration}>
          <Illustration illustration={illustrationKey} />
        </View>
      </View>
    </PressableWithDelay>
  );
};

const styles = StyleSheet.create({
  border: {
    borderColor: theme.color.border.primary,
  },
  buttonTextContainer: {
    borderColor: theme.color.interactive.primary,
    borderRadius: 4,
    borderWidth: 1,
    color: theme.color.interactive.primary,
    marginTop: theme.spacing.xs,
    paddingHorizontal: 12,
    paddingVertical: 4,
  },
  container: {
    backgroundColor: theme.color.interactive.secondary,
    borderColor: 'transparent', //TODO: update border to outline after RN upgrade 0.78
    borderRadius: 4,
    borderWidth: 1,
    flexDirection: 'row',
    justifyContent: 'space-between',
  },
  disabledBorder: {
    borderColor: theme.color.interactive.disabled.border,
  },
  disabledText: {
    color: theme.color.interactive.disabled.text,
  },
  focused: {
    outlineColor: theme.color.interactive.focused.border,
    outlineWidth: 1,
  },
  hovered: {
    backgroundColor: theme.color.interactive.secondaryHovered,
  },
  illustration: {
    bottom: 0,
    height: 85,
    position: 'absolute',
    right: 0,
    width: 85,
  },
  illustrationBackground: {
    borderBottomRightRadius: 4,
    borderTopRightRadius: 4,
    flex: 1,
    width: 58,
  },
  illustrationContainer: {
    alignItems: 'flex-end',
    marginRight: theme.spacing.s,
    marginVertical: theme.spacing.s,
    width: 93,
  },
  pressed: {
    backgroundColor: theme.color.interactive.secondaryPressed,
  },
  textContainer: {
    alignItems: 'flex-start',
    flex: 1,
    gap: theme.spacing.xs,
    justifyContent: 'center',
    paddingLeft: theme.spacing.m,
    paddingRight: theme.spacing.s,
    paddingVertical: theme.spacing.m,
  },
});

export default ProductOfferCard;
